/**
 * Component | Decision Tree
 * Manages step-based navigation through decision tree steps
 */

import { IModule, component } from "@sygnal/sse";

@component('decision-tree')
export class DecisionTreeComponent implements IModule {
  private elem: HTMLElement;
  private stepElements: HTMLElement[] = [];
  private isPopping: boolean = false;

  constructor(elem: HTMLElement) {
    this.elem = elem;
  }

  setup(): void {
  }

  async exec(): Promise<void> {
    // Find immediate div child and collect step elements
    const immediateDiv = this.elem.querySelector(':scope > div');

    if (immediateDiv) {
      this.stepElements = Array.from(
        immediateDiv.querySelectorAll('[step-id]')
      ) as HTMLElement[];
    }

    // Hide all step elements initially
    this.stepElements.forEach(stepElem => {
      stepElem.style.display = 'none';
    });

    // Bind hash link click events
    this.bindEvents();

    // Handle browser back/forward
    window.addEventListener('popstate', (event: PopStateEvent) => {
      const stepName = event.state?.step || 'start';
      this.isPopping = true;
      this.navigateToStep(stepName);
      this.isPopping = false;
    });

    // Determine initial step from URL hash or default to 'start'
    const initialStep = window.location.hash
      ? window.location.hash.substring(1)
      : 'start';

    // Replace current history entry with the initial step state
    history.replaceState({ step: initialStep }, '', `#${initialStep}`);
    this.navigateToStep(initialStep);
  }

  private bindEvents(): void {
    this.elem.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href^="#"]');

      if (link) {
        event.preventDefault();
        const hash = (link as HTMLAnchorElement).getAttribute('href');
        if (hash === '#back') {
          history.back();
        } else if (hash && hash.startsWith('#')) {
          const stepName = hash.substring(1);
          this.navigateToStep(stepName);
        }
      }
    });
  }

  navigateToStep(name: string): void {
    // Find the first element with matching step-id
    const targetStep = this.stepElements.find(
      elem => elem.getAttribute('step-id') === name
    );

    // Hide all steps
    this.stepElements.forEach(stepElem => {
      stepElem.style.display = 'none';
    });

    // Show only the target step if found
    if (targetStep) {
      targetStep.style.display = '';
    }

    // Show/hide nav based on step
    const nav = document.querySelector<HTMLElement>('[app="nav"]');
    if (nav) {
      nav.style.display = name === 'start' ? '' : 'none';
    }

    // Push to browser history only for user-initiated navigation (not back/forward)
    if (!this.isPopping) {
      history.pushState({ step: name }, '', `#${name}`);
    }
  }
}
